import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { userNeedsEmailVerification } from '@/services/auth';
import { auth } from '@/services/firebase';
import {
  ensureInviteLookup,
  mergeInviteLookupPublicProfile,
  publishUserChatPublicKey,
} from '@/services/chatCryptoFirestore';
import {
  clearSessionChatKeyPair,
  exportChatPublicKeyJwk,
  getOrCreateChatKeyPair,
  setSessionChatKeyPair,
} from '@/services/chatCryptoKeys';
import {
  loadChatIdentityFromFirestore,
  persistChatIdentityBackup,
} from '@/services/chatIdentityFirestore';
import { clearSessionInviteToken, setSessionInviteToken } from '@/services/chatSessionIdentity';
import { resolvedDisplayNameForForm } from '@/services/userDisplayName';

const ChatCryptoPublishErrorContext = createContext<string | null>(null);
const ChatIdentityContext = createContext<string>('');

export function useMyInviteToken(): string {
  return useContext(ChatIdentityContext);
}

/**
 * Загружает invite + ECDH из Firestore в сессию (без localStorage), публикует lookup и ключи.
 */
export function ChatCryptoBootstrapProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { currentUser, userData } = useAuth() as {
    currentUser: {
      uid: string;
      email: string | null;
      displayName: string | null;
      photoURL: string | null;
    } | null;
    userData: Record<string, unknown> | null;
  };
  const skipBootstrap =
    !!currentUser && userNeedsEmailVerification(currentUser);
  const uid = skipBootstrap ? '' : (currentUser?.uid ?? '');

  const [identityReady, setIdentityReady] = useState(false);
  const [myInviteToken, setMyInviteToken] = useState('');
  const [publishError, setPublishError] = useState<string | null>(null);
  /** Смена вкладки / окна — повторно пушим inviteLookup (Firestore userData часто приходит позже Auth). */
  const [republishTick, setRepublishTick] = useState(0);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        setRepublishTick((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!uid) {
      setMyInviteToken('');
      setIdentityReady(true);
      setPublishError(null);
      return;
    }

    let cancelled = false;
    setIdentityReady(false);
    setMyInviteToken('');
    setPublishError(null);

    void (async () => {
      const delays = [0, 700, 1500, 2800, 4500];
      let lastErr: unknown = null;

      for (let i = 0; i < delays.length && !cancelled; i++) {
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
        }
        if (cancelled) return;
        try {
          /**
           * После OAuth-redirect токен auth иногда запаздывает относительно Firestore.
           * Перед повторной попыткой принудительно обновляем ID token.
           */
          if (i > 0) {
            await auth.currentUser?.getIdToken(true);
          }
          const { inviteToken, keyPair } = await loadChatIdentityFromFirestore(uid);
          if (cancelled) return;
          setSessionInviteToken(uid, inviteToken);
          setSessionChatKeyPair(uid, keyPair);
          setMyInviteToken(inviteToken);
          setPublishError(null);
          return;
        } catch (e) {
          lastErr = e;
          console.warn('[ChatCryptoBootstrap] loadChatIdentity attempt', i + 1, e);
        }
      }

      if (!cancelled) {
        setPublishError(lastErr instanceof Error ? lastErr.message : String(lastErr));
      }
    })()
      .finally(() => {
        if (!cancelled) setIdentityReady(true);
      });

    return () => {
      cancelled = true;
      clearSessionInviteToken(uid);
      clearSessionChatKeyPair(uid);
    };
  }, [uid]);

  useEffect(() => {
    if (!uid || !myInviteToken || !identityReady) {
      setPublishError(null);
      return;
    }
    let cancelled = false;

    const runOnce = async (): Promise<void> => {
      await ensureInviteLookup(uid, myInviteToken);
      const firestoreName =
        typeof userData?.displayName === 'string' ? userData.displayName : undefined;
      const displayName = resolvedDisplayNameForForm(firestoreName, currentUser).trim();
      const photoFromProfile =
        typeof userData?.photoURL === 'string' && userData.photoURL.trim()
          ? userData.photoURL.trim()
          : null;
      const photoURL = photoFromProfile ?? currentUser?.photoURL ?? null;
      await mergeInviteLookupPublicProfile(uid, myInviteToken, {
        /* Не передаём пустое имя: иначе merge сотрёт displayName в inviteLookup до прихода userData из Firestore. */
        ...(displayName ? { displayName } : {}),
        photoURL,
        email: currentUser?.email ?? null,
        discoverableInPeopleSearch: userData?.discoverableInPeopleSearch !== false,
      });
      const pair = await getOrCreateChatKeyPair(uid);
      const pubJwk = await exportChatPublicKeyJwk(pair.publicKey);
      const privateJwk = (await crypto.subtle.exportKey('jwk', pair.privateKey)) as JsonWebKey;
      if (!cancelled) await publishUserChatPublicKey(uid, pubJwk);
      if (!cancelled) {
        try {
          await persistChatIdentityBackup(uid, myInviteToken, { privateJwk, publicJwk: pubJwk });
        } catch (e) {
          console.warn('[ChatCryptoBootstrap] persistChatIdentityBackup', e);
        }
      }
    };

    void (async () => {
      const delays = [0, 900, 2100];
      let lastErr: unknown;
      for (let i = 0; i < delays.length && !cancelled; i++) {
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
        }
        if (cancelled) return;
        try {
          setPublishError(null);
          await runOnce();
          return;
        } catch (e) {
          lastErr = e;
          console.warn('[ChatCryptoBootstrap] publish attempt', i + 1, e);
        }
      }
      if (!cancelled) {
        setPublishError(lastErr instanceof Error ? lastErr.message : String(lastErr));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uid,
    myInviteToken,
    identityReady,
    republishTick,
    currentUser?.displayName,
    currentUser?.photoURL,
    currentUser?.email,
    userData,
  ]);

  if (uid && !identityReady) {
    return (
      <div
        className="text-muted-foreground flex min-h-[40vh] w-full items-center justify-center px-4 text-center text-sm"
        role="status"
        aria-live="polite"
      >
        <p>{t('chats.chatBootstrapPreparing')}</p>
      </div>
    );
  }

  if (uid && identityReady && !myInviteToken) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] w-full flex-col items-center justify-center gap-2 px-4 text-center text-sm">
        <p>{t('chats.chatBootstrapInitFailed')}</p>
        {publishError ? <p className="text-destructive text-xs">{publishError}</p> : null}
      </div>
    );
  }

  return (
    <ChatIdentityContext.Provider value={myInviteToken}>
      <ChatCryptoPublishErrorContext.Provider value={publishError}>
        {children}
      </ChatCryptoPublishErrorContext.Provider>
    </ChatIdentityContext.Provider>
  );
}

export function useChatCryptoPublishError(): string | null {
  return useContext(ChatCryptoPublishErrorContext);
}
